import {
  AppWidget,
  ComparisonTable,
  type ComparisonTableProps,
  ConfidenceBar,
  ExpertSection,
  ExpertsReviews,
  GuaranteeSection,
  VideoButtonWrapper,
  comparisonTableData,
  transformToComparisonTableData
} from '@ecomm/scout'
import {
  AffirmBanner,
  AlarmMoments,
  PageWrapper,
  TrustpilotBanner,
  TrustpilotUKTopBanner,
  UserReviews,
  trustPilotUKHomePageCarousel
} from '@ecomm/shared-components'
import {
  safeMapExperiences,
  useCameraOnlyLPCTAExperience
} from '@ecomm/shared-ninetailed'
import { Experience } from '@ecomm/shared-ninetailed-experience'
import { useEnv } from '@ecomm/utils'
import { userReviewsData } from './constants'

import {
  appRatingImages,
  getAppWidgetData,
  getExpertReviews
} from '../../data/transformData'
import { useStaticQueries } from '../../data/util'
import { QuizBanner } from '../QuizBanner'
import { VideoBannerExperiment } from '../VideoBannerExperiment'
import type { LanderBelowTheFoldSchema } from './schema'

type LanderBelowTheFoldContent = {
  readonly data: LanderBelowTheFoldSchema
}

type ExperienceContentProps = {
  readonly id: string
  readonly json: { readonly tag: string }
  readonly children: React.ReactNode
  readonly variantKey: string
}

export function ExperienceContentWrapper({
  json,
  children,
  variantKey
}: ExperienceContentProps) {
  return json.tag === variantKey ? children : null
}

export function BelowTheFold({ data }: LanderBelowTheFoldContent) {
  const { locale } = useEnv()
  const isUSSite = locale === 'en-US'

  const {
    expertReviewsData,
    expertSectionAssetData,
    affirmBannerData,
    videoButtonAssetData,
    appWidget,
    confidenceBarData,
    guaranteeAsset,
    guaranteeCopy,
    quoteWizardAsset
  } = useStaticQueries()

  const { isCameraOnlyVariant } = useCameraOnlyLPCTAExperience()

  const expertReviews = getExpertReviews(
    data.expertReviewsAssets.items,
    locale,
    expertReviewsData.content.reviews
  )

  const appWidgetData = {
    tabs: getAppWidgetData(
      data.appFeaturesCarouselAssets.items,
      locale,
      appWidget.tabs
    ),
    title: appWidget.title,
    appRatingImages
  }
  const transformedComparisonTableData: ComparisonTableProps =
    transformToComparisonTableData(comparisonTableData[locale])

  const videoAndAffirmBanner = (
    <div className="grid h-36 gap-4 md:h-80 lg:h-full lg:grid-cols-2 lg:gap-8">
      <VideoButtonWrapper videoButtonAsset={videoButtonAssetData} />
      <AffirmBanner data={affirmBannerData} />
    </div>
  )

  return (
    <PageWrapper>
      <div
        className="max-w-8xl mx-auto mb-16 flex flex-col gap-6 px-4 md:gap-8 md:px-8 lg:gap-12"
        data-testid="BelowTheFold"
      >
        {isUSSite ? (
          <>
            {data.hideVideoAndAffirmBannerExperiment ? (
              <Experience
                {...data.hideVideoAndAffirmBannerExperiment}
                component={ExperienceContentWrapper}
                experiences={safeMapExperiences<ExperienceContentProps>(
                  data.hideVideoAndAffirmBannerExperiment
                )}
                passthroughProps={{
                  children: videoAndAffirmBanner,
                  variantKey: 'control'
                }}
              />
            ) : (
              videoAndAffirmBanner
            )}

            <ConfidenceBar data={confidenceBarData.content} />
            {!isCameraOnlyVariant ? (
              <QuizBanner
                backgroundColor="bg-[#D2D2D2]"
                image={quoteWizardAsset}
                quoteWizard={data.quoteWizard.quoteWizard}
              />
            ) : null}
            <VideoBannerExperiment data={data} />
          </>
        ) : (
          <>
            <TrustpilotUKTopBanner />
            <ConfidenceBar data={confidenceBarData.content} />
            <QuizBanner
              image={quoteWizardAsset}
              quoteWizard={data.quoteWizard.quoteWizard}
            />
            <TrustpilotBanner
              className="pt-4 md:pt-8"
              widget={trustPilotUKHomePageCarousel}
            />
          </>
        )}

        <ComparisonTable data={transformedComparisonTableData} />
        {!isUSSite && data.alarmMoments ? (
          <AlarmMoments {...data.alarmMoments} />
        ) : null}
        <AppWidget data={{ ...appWidgetData }} />
        <UserReviews {...userReviewsData} />
        <ExpertsReviews reviews={expertReviews} />
        {data.expertReviewsAdditional && data.expertReviewsAdditionalAssets ? (
          <ExpertsReviews
            reviews={getExpertReviews(
              data.expertReviewsAdditionalAssets.items,
              locale,
              data.expertReviewsAdditional.content.reviews
            )}
          />
        ) : null}
        <GuaranteeSection
          copy={new Map<string, string>(guaranteeCopy)}
          image={guaranteeAsset.guaranteeAsset}
          quoteWizard={data.quoteWizard.quoteWizard}
        />
        <ExpertSection data={{ image: expertSectionAssetData }} />
      </div>
    </PageWrapper>
  )
}
